import { z } from 'zod';
import { route, defineContract } from '../core/types';
import { requiredStringSchema, calendarDateField, tzHeadersSchema, sseQuerySchema, coercedIdSchema } from '../schemas';

// ============================================================
// Param Schemas
// ============================================================

const projectParamsSchema = z.object({
	projectId: requiredStringSchema.describe('Project identifier'),
});

const milestoneParamsSchema = projectParamsSchema.extend({
	milestoneId: requiredStringSchema.describe('Milestone ID'),
});

// ============================================================
// Contract for 8 non-SSE milestone routes
// ============================================================

/**
 * IMPORTANT: Route definition order must match registration order.
 * deleteMultipleMilestones BEFORE deleteMilestone (since /multiple must register
 * before /:milestoneId).
 *
 * `{ root: true }` because the project has to precede the collection segment,
 * which a contract-name prefix cannot express. The three routes that keep their
 * old URLs — the two cross-project reads and the SSE stream — therefore spell
 * `/milestones` themselves; without that, making the contract root would have
 * silently moved them.
 */
export const milestoneContract = defineContract(
	'milestones',
	{
		getProjectsMilestones: route({
			method: 'GET',
			path: '/milestones/projectsMilestones',
			query: z.object({
				organizationId: requiredStringSchema.describe('Organization ID'),
				projectIds: requiredStringSchema.describe('Comma-separated project IDs'),
			}),
			response: z.any(),
			operationId: 'getProjectsMilestones',
			tags: ['Milestones'],
			summary: 'Get milestones for multiple projects',
			description: 'Retrieves milestones for the specified projects within an organization',
		}),
		getProjectStatus: route({
			method: 'GET',
			path: '/milestones/projectStatus/:projectId',
			params: z.object({
				projectId: requiredStringSchema.describe('Project identifier'),
			}),
			headers: tzHeadersSchema,
			response: z.any(),
			operationId: 'getProjectStatusById',
			tags: ['Milestones'],
			summary: 'Get project progress status',
			description: 'Retrieves the project progress status (late, minorDelay, onTrack, noDataAvailable)',
		}),
		getMilestonesByProject: route({
			method: 'GET',
			path: '/projects/:projectId/milestones',
			params: projectParamsSchema,
			response: z.any(),
			operationId: 'getMilestonesByProjectId',
			tags: ['Milestones'],
			summary: 'Get all milestones for a project',
			description: 'Retrieves all milestones associated with the specified project',
		}),
		getMilestoneById: route({
			method: 'GET',
			path: '/projects/:projectId/milestones/:milestoneId',
			params: milestoneParamsSchema,
			response: z.any(),
			operationId: 'getMilestoneById',
			tags: ['Milestones'],
			summary: 'Get a milestone by ID',
			description: 'Retrieves a specific milestone by its ID. Returns null if not found.',
		}),
		updateMilestone: route({
			method: 'PUT',
			path: '/projects/:projectId/milestones/:milestoneId',
			params: milestoneParamsSchema,
			// `projectId` omitted deliberately: accepting it would let an update move a
			// milestone between projects.
			body: z.object({
				_id: coercedIdSchema.optional(),
				title: z.string().optional(),
				isDone: z.boolean().optional(),
				endDate: calendarDateField.optional(),
			}),
			response: z.any(),
			operationId: 'updateMilestoneById',
			tags: ['Milestones'],
			summary: 'Update a milestone',
			description: 'Updates an existing milestone by ID. All fields are optional.',
		}),
		deleteMultipleMilestones: route({
			method: 'DELETE',
			path: '/projects/:projectId/milestones/multiple',
			params: projectParamsSchema,
			body: z.object({
				ids: z.array(z.string()),
			}),
			response: z.any(),
			operationId: 'deleteMilestonesById',
			tags: ['Milestones'],
			summary: 'Delete multiple milestones',
			description: 'Deletes multiple milestones by their IDs',
		}),
		deleteMilestone: route({
			method: 'DELETE',
			path: '/projects/:projectId/milestones/:milestoneId',
			params: milestoneParamsSchema,
			response: z.any(),
			operationId: 'deleteMilestoneById',
			tags: ['Milestones'],
			summary: 'Delete a milestone',
			description: 'Deletes a milestone by ID',
		}),
		createMilestone: route({
			method: 'POST',
			path: '/projects/:projectId/milestones',
			params: projectParamsSchema,
			// No `projectId`: the path carries it, and it is the value the guard verified.
			body: z.object({
				_id: coercedIdSchema.optional(),
				title: requiredStringSchema.describe('Milestone title'),
				isDone: z.boolean().describe('Completion status'),
				endDate: calendarDateField.describe('End date'),
			}),
			headers: tzHeadersSchema,
			response: z.any(),
			operationId: 'createMilestone',
			tags: ['Milestones'],
			summary: 'Create a new milestone',
			description: 'Creates a new milestone for the specified project',
		}),
	},
	{ root: true }
);

// ============================================================
// SSE route (cannot use implementContract - needs direct fastify.sse access)
// ============================================================

export const milestoneStreamRoute = route({
	method: 'GET',
	path: '/milestones/projectsMilestones/stream',
	query: sseQuerySchema.extend({
		organizationId: requiredStringSchema.describe('Organization ID'),
		projectIds: requiredStringSchema.describe('Comma-separated project IDs'),
	}),
	response: z.any().describe('SSE stream'),
	operationId: 'getMilestonesStream',
	tags: ['Milestones'],
	summary: 'Stream milestones for multiple projects (SSE)',
	description: 'Server-Sent Events stream for real-time milestone updates across multiple projects',
});
